import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Inbox, Loader2, ChevronDown, Check, X, Flag } from 'lucide-react';
import { useInbox, useInboxAssign, useInboxDismiss, type InboxTask } from '@/hooks/use-inbox';
import { useUpdateTask } from '@/hooks/use-tasks';
import { useWorkspaces } from '@/hooks/use-workspaces';
import { useProjects, type ProjectItem } from '@/hooks/use-projects';
import { usePullToRefresh } from '@/hooks/use-pull-to-refresh';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { differenceInDays, parseISO, format } from 'date-fns';

export const Route = createFileRoute('/_authenticated/inbox')({
  component: InboxPage,
});

// ── Priority Badge ──────────────────────────────────────────────────────

const PRIORITY_COLORS: Record<string, string> = {
  low: 'text-blue-500',
  medium: 'text-amber-500',
  high: 'text-orange-500',
  urgent: 'text-red-500',
};

function PriorityBadge({ priority }: { priority: string }) {
  if (!priority || priority === 'none') return null;
  return (
    <span className={cn('flex items-center gap-0.5 text-[10px]', PRIORITY_COLORS[priority])}>
      <Flag className="h-3 w-3" />
      {priority}
    </span>
  );
}

// ── Assign Project Picker ───────────────────────────────────────────────

function AssignProjectPicker({ task, onAssign }: { task: InboxTask; onAssign: (projectId: string) => void }) {
  const { data: workspaces } = useWorkspaces();
  const [expandedWs, setExpandedWs] = useState<string | null>(null);
  const { data: projects } = useProjects(expandedWs ?? '');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 gap-1 text-xs">
          Assign
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {workspaces?.map((ws) => (
          <div key={ws.id}>
            <DropdownMenuLabel
              className="cursor-pointer text-xs hover:bg-accent rounded-sm"
              onClick={() => setExpandedWs(expandedWs === ws.id ? null : ws.id)}
            >
              {ws.name}
            </DropdownMenuLabel>
            {expandedWs === ws.id && projects && (
              <>
                {projects
                  .filter((p: ProjectItem) => !p.archivedAt)
                  .map((p: ProjectItem) => (
                    <DropdownMenuItem
                      key={p.id}
                      className="pl-6 text-xs"
                      onClick={() => onAssign(p.id)}
                    >
                      {p.name}
                      <span className="ml-auto text-[10px] capitalize text-muted-foreground">
                        {p.isParked ? 'parked' : 'active'}
                      </span>
                    </DropdownMenuItem>
                  ))}
                {projects.filter((p: ProjectItem) => !p.archivedAt).length === 0 && (
                  <div className="px-6 py-2 text-xs text-muted-foreground">No projects</div>
                )}
              </>
            )}
          </div>
        ))}
        {(!workspaces || workspaces.length === 0) && (
          <div className="px-4 py-2 text-xs text-muted-foreground">No workspaces</div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Inbox Task Row ──────────────────────────────────────────────────────

function InboxTaskRow({
  task,
  section,
}: {
  task: InboxTask;
  section: 'unassigned' | 'overdue';
}) {
  const updateTask = useUpdateTask(task.workspaceId);
  const assignMutation = useInboxAssign();
  const dismissMutation = useInboxDismiss();

  const isDone = task.status === 'done';

  const handleToggle = () => {
    updateTask.mutate({
      taskId: task.id,
      status: isDone ? 'todo' : 'done',
    });
  };

  const handleAssign = (projectId: string) => {
    assignMutation.mutate({ taskId: task.id, project_id: projectId });
  };

  const handleDismiss = () => {
    dismissMutation.mutate(task.id);
  };

  return (
    <div className="group flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/50">
      <Checkbox
        checked={isDone}
        onCheckedChange={handleToggle}
        className="shrink-0"
      />

      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'text-sm font-medium leading-snug truncate',
            isDone && 'line-through text-muted-foreground'
          )}
        >
          {task.title}
        </p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <Badge variant="outline" className="h-4 px-1.5 text-[9px] font-normal">
            {task.workspaceName}
          </Badge>
          <PriorityBadge priority={task.priority} />
          {section === 'overdue' && task.dueDate && (
            <span className="text-[10px] text-red-500 font-medium">
              {differenceInDays(new Date(), parseISO(task.dueDate))} days overdue
            </span>
          )}
          {task.dueDate && section !== 'overdue' && (
            <span className="text-[10px] text-muted-foreground">
              Due {format(parseISO(task.dueDate), 'MMM d')}
            </span>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1.5 opacity-0 transition-opacity group-hover:opacity-100">
        {section === 'unassigned' && (
          <AssignProjectPicker task={task} onAssign={handleAssign} />
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-muted-foreground hover:text-green-600"
          title="Mark done"
          onClick={() => updateTask.mutate({ taskId: task.id, status: 'done' })}
        >
          <Check className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600"
          title="Dismiss"
          onClick={handleDismiss}
          disabled={dismissMutation.isPending}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ── Inbox Section ───────────────────────────────────────────────────────

function InboxSection({
  title,
  tasks,
  section,
  color,
}: {
  title: string;
  tasks: InboxTask[];
  section: 'unassigned' | 'overdue';
  color: 'purple' | 'red';
}) {
  if (tasks.length === 0) return null;

  const colorMap = {
    purple: {
      dot: 'bg-purple-500',
      border: 'border-purple-200',
      countBg: 'bg-purple-100 text-purple-700',
    },
    red: {
      dot: 'bg-red-500',
      border: 'border-red-200',
      countBg: 'bg-red-100 text-red-700',
    },
  };

  const colors = colorMap[color];

  return (
    <div className={cn('rounded-lg border', colors.border)}>
      <div className="flex items-center gap-2 px-4 py-3">
        <div className={cn('h-2 w-2 rounded-full', colors.dot)} />
        <h2 className="text-sm font-semibold">{title}</h2>
        <Badge className={cn('h-5 min-w-5 justify-center px-1.5 text-[10px] font-medium', colors.countBg)}>
          {tasks.length}
        </Badge>
      </div>
      <div className="divide-y">
        {tasks.map((task) => (
          <InboxTaskRow key={task.id} task={task} section={section} />
        ))}
      </div>
    </div>
  );
}

// ── Inbox Page ──────────────────────────────────────────────────────────

function InboxPage() {
  const { data, isLoading } = useInbox();
  const { isRefreshing } = usePullToRefresh({ queryKeys: [['inbox']] });

  const unassigned = data?.unassigned ?? [];
  const overdue = data?.overdue ?? [];
  const totalCount = unassigned.length + overdue.length;

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <div className="h-7 w-28 rounded bg-muted animate-pulse mb-1" />
          <div className="h-4 w-48 rounded bg-muted animate-pulse" />
        </div>
        {[0, 1].map((i) => (
          <div key={i} className="rounded-lg border p-4 space-y-3">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-muted animate-pulse" />
              <div className="h-4 w-20 rounded bg-muted animate-pulse" />
            </div>
            {Array.from({ length: 3 }).map((_, j) => (
              <div key={j} className="flex items-center gap-3 px-3 py-2">
                <div className="h-4 w-4 rounded bg-muted animate-pulse" />
                <div className="flex-1 space-y-1">
                  <div className="h-4 w-3/5 rounded bg-muted animate-pulse" />
                  <div className="h-3 w-1/4 rounded bg-muted animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {isRefreshing && (
        <div className="flex items-center justify-center py-2 md:hidden">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <span className="ml-2 text-xs text-muted-foreground">Refreshing...</span>
        </div>
      )}

      <div className="flex items-center gap-3">
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Inbox className="h-6 w-6" />
          Inbox
        </h1>
        {totalCount > 0 && (
          <Badge variant="secondary" className="text-xs">
            {totalCount}
          </Badge>
        )}
      </div>

      {totalCount === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed py-16 px-6 text-center">
          <Inbox className="h-12 w-12 text-muted-foreground/30" />
          <p className="mt-4 text-lg font-medium text-muted-foreground">
            All clear — inbox zero!
          </p>
          <p className="mt-1 text-sm text-muted-foreground/70">
            No tasks need your attention right now.
          </p>
        </div>
      ) : (
        <>
          <InboxSection
            title="Unassigned"
            tasks={unassigned}
            section="unassigned"
            color="purple"
          />
          <InboxSection
            title="Overdue"
            tasks={overdue}
            section="overdue"
            color="red"
          />
        </>
      )}
    </div>
  );
}
